import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { noop } from "lodash";
import clsx from "clsx";

import { SelectionFeature, SelectionType } from "src/js/stores/map/store";
import { ScopeType } from "src/js/stores/site/store";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { useTrackedApplicationSelectionHandler } from "react-migration/domains/planning/utils/useTrackedApplicationSelectionHandler";
import { PlanningApplication } from "react-migration/domains/planning/types";
import { useRadiusInput } from "react-migration/lib/map/useRadiusInput";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { TextInputV2 } from "react-migration/components/TextInputV2";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";
import { DividerV2 } from "react-migration/components/DividerV2";
import { CardTabs } from "react-migration/layouts/map/Multilayer/Bundle/Workbench/AssessmentTab/CardTabs";
import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";

import { Layer, LayerTypeSelectionSidebarHeaderProps, LayerTypeSelectionSidebarProps } from "../../../types";
import { CollapsibleConsiderationsCard } from "../../../Bundle/Workbench/AssessmentTab/CollapsibleConsiderationsCard";
import { useSelectionSidebarLoading } from "../../../Bundle/useSelectionSidebarLoadingState";
import { useFilterStateTracking } from "../../../hooks/useFilterStateTracking";
import { SearchIcon } from "../../components/SearchIcon";
import { PlanningApplicationsByAddress } from "../PlanningApplicationsByAddress";
import { SummaryGenerate } from "../PlanningApplicationsSummary/components/SummaryGenerate";
import { PlanningApplicationsFilters } from "../components/PlanningApplicationsFilters";
import { PlanningState, PlanningStateDispatch, usePlanningFilterState } from "../state/usePlanningFilterState";
import { useClassAndTypeFilters } from "../PlanningApplicationsProvider/hooks";
import { ClassificationToggles } from "../components/ClassificationToggles";
import { PlanningLayerConfig } from "../types/layerTypes";
import { PlanningApplicationsSummary } from "../PlanningApplicationsSummary";
import { usePlanningApplicationListBySelection } from "./usePlanningApplicationListBySelection";

export function PlanningApplicationSelectionSidebar({
  layer,
  selection,
  setDetailSelection,
  onLoadingChange,
  cardExpanded,
}: Readonly<LayerTypeSelectionSidebarProps>) {
  const planningLayerConfig = (layer?.layerConfig ?? {}) as PlanningLayerConfig;
  const [state, stateDispatch, { hasDefaultFilters, hasNoFilters }] = usePlanningFilterState(
    planningLayerConfig.localStorageKey,
    planningLayerConfig.initialFilterState
  );
  const [, planningApplicationLayerFilters] = useClassAndTypeFilters(state);
  const [search, setSearch] = useState("");

  const [tab, setTab] = useState<ScopeType>(ScopeType.ON_SITE);
  const { RadiusInput, bufferedGeometry } = useRadiusInput({
    geometry: selection?.feature?.geometry ?? null,
    enabled: tab === ScopeType.NEARBY,
    defaultRadius: 100,
  });

  const siteData = usePlanningApplicationListBySelection(selection, {
    filters: planningApplicationLayerFilters,
  });
  const nearbyData = usePlanningApplicationListBySelection(
    { ...selection, feature: bufferedGeometry },
    { filters: planningApplicationLayerFilters }
  );

  const filteredByTitleApplications = useMemo(() => {
    return siteData.filteredPlanningApplications.filter((application) =>
      application.title.toLowerCase().includes(search.toLowerCase())
    );
  }, [siteData.filteredPlanningApplications, search]);

  const filteredByTitleNearbyApplications = useMemo(() => {
    return nearbyData.filteredPlanningApplications.filter((application) =>
      application.title.toLowerCase().includes(search.toLowerCase())
    );
  }, [nearbyData.filteredPlanningApplications, search]);

  useSelectionSidebarLoading(onLoadingChange, siteData.loading && nearbyData.loading);

  // Error is handled at boundary level, so we log it here
  if (siteData.error) console.error(siteData.error);
  if (nearbyData.error) console.error(nearbyData.error);

  return (
    <div key={selection.id} className="flex flex-col gap-y-2">
      {!siteData.error && siteData.initialApplications.length > 0 && hasFeature(Feature.planningHistorySummary) && (
        <AISummaryCard
          selection={selection}
          title={layer.title}
          referenceApplications={siteData.initialApplications}
          setDetailSelection={setDetailSelection}
        />
      )}
      <CollapsibleConsiderationsCard heading={layer.title} name={layer.title} defaultExpanded={cardExpanded}>
        <CardTabs
          tab={tab}
          onTabChange={setTab}
          resultsCountName="Consideration"
          siteResults={{
            filtered: filteredByTitleApplications.length,
            all: siteData.initialApplications.length,
            loading: siteData.loading,
          }}
          nearbyResults={{
            filtered: filteredByTitleNearbyApplications.length,
            all: nearbyData.initialApplications.length,
            loading: nearbyData.loading,
          }}
        >
          <CardTabs.SiteTab
            error={siteData.error}
            loading={siteData.loading}
            resultsCount={filteredByTitleApplications.length}
            setTab={setTab}
          >
            <PlanningApplicationFilters
              layer={layer}
              hasNoFilters={hasNoFilters}
              search={search}
              setSearch={setSearch}
              stateDispatch={stateDispatch}
              state={state}
              hasDefaultFilters={hasDefaultFilters}
            />
            <PlanningApplicationsResults
              layer={layer}
              stateDispatch={stateDispatch}
              applications={filteredByTitleApplications}
              hasNoFilters={hasNoFilters}
              setDetailSelection={setDetailSelection}
              onLoadingChange={onLoadingChange}
              cardExpanded={cardExpanded}
              onSelection={noop}
            />
          </CardTabs.SiteTab>
          <CardTabs.NearbyTab
            error={nearbyData.error}
            loading={nearbyData.loading}
            resultsCount={filteredByTitleNearbyApplications.length}
            radiusInput={RadiusInput}
          >
            <PlanningApplicationFilters
              layer={layer}
              hasNoFilters={hasNoFilters}
              search={search}
              setSearch={setSearch}
              stateDispatch={stateDispatch}
              state={state}
              hasDefaultFilters={hasDefaultFilters}
            />
            <PlanningApplicationsResults
              layer={layer}
              stateDispatch={stateDispatch}
              applications={filteredByTitleNearbyApplications}
              hasNoFilters={hasNoFilters}
              setDetailSelection={setDetailSelection}
              onLoadingChange={onLoadingChange}
              cardExpanded={cardExpanded}
              onSelection={noop}
            />
          </CardTabs.NearbyTab>
        </CardTabs>
      </CollapsibleConsiderationsCard>
    </div>
  );
}

const AISummaryCard = ({
  selection,
  title,
  referenceApplications,
  setDetailSelection,
}: {
  selection: SelectionFeature;
  title: string;
  referenceApplications: PlanningApplication[];
  setDetailSelection: (selection: SelectionFeature) => void;
}) => {
  const { t } = useTranslation();
  const [showSummary, setShowSummary] = useState(false);

  const trackedSetApplicationDetailSelectionHandler = useCallback(
    (selection: SelectionFeature) => {
      setDetailSelection(selection);

      logEventV2({
        name: WorkbenchEventName.WORKBENCH_CARD_CLICK,
        properties: {
          cardName: t("planning.ai.title"),
          clickDetail: "Planning application",
          action: "goTo",
          planningApplicationID: selection.id,
        },
      });
    },
    [setDetailSelection, t]
  );

  return (
    <CollapsibleConsiderationsCard
      heading={
        <>
          <Icon size="md" icon="icon-lt-sparkling-line text-platform-500" />
          <h1 className="text-sm font-semibold text-text-base-primary">{t("planning.ai.title")}</h1>
        </>
      }
      name="planning applications ai summary"
      defaultExpanded={true}
    >
      {showSummary ? (
        <PlanningApplicationsSummary
          selection={selection}
          referenceApplications={referenceApplications}
          setDetailSelection={trackedSetApplicationDetailSelectionHandler}
        />
      ) : (
        <SummaryGenerate setShowSummary={setShowSummary} cardName={title} />
      )}
    </CollapsibleConsiderationsCard>
  );
};

const PlanningApplicationFilters = ({
  layer,
  hasNoFilters,
  search,
  setSearch,
  state,
  hasDefaultFilters,
  stateDispatch,
}: {
  layer: Layer;
  hasNoFilters: boolean;
  search: string;
  setSearch: (v: string) => void;
  state: PlanningState;
  hasDefaultFilters: boolean;
  stateDispatch: PlanningStateDispatch;
}) => {
  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value), []);
  const { debouncedTrackControlChange } = useFilterStateTracking(layer.title);
  const [showFilter, setShowFilter] = useState(false);

  const handleShowFilter = useCallback(() => {
    logEventV2({
      name: WorkbenchEventName.WORKBENCH_FILTER_CLICK,
      properties: {
        cardName: layer.title,
        filterName: "Show",
        filterState: !showFilter,
      },
    });
    setShowFilter(!showFilter);
  }, [layer.title, showFilter, setShowFilter]);

  return (
    <>
      <div className="flex items-center p-2 space-x-2">
        <div className="grow">
          <TextInputV2
            onChange={handleInputChange}
            dataTestId="labels-search-input"
            value={search}
            placeholder="Description search..."
            IconLeft={
              <div className="w-5 h-5 text-blue-grey-800">
                <SearchIcon />
              </div>
            }
          />
        </div>
        <Button data-testid="filter-planning" variant="tertiary" onClick={handleShowFilter}>
          <Icon icon="icon-lt-filter-line" extraClasses="text-neutral-500 pointer-events-none" />
          {showFilter && <span className="text-sm font-medium">Close</span>}
          {!hasNoFilters && !showFilter && <div className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" />}
        </Button>
      </div>
      {showFilter && (
        <div className="gap-4 p-2">
          <div className="flex justify-end gap-2">
            <button
              disabled={hasDefaultFilters}
              className="flex items-center justify-end h-5 gap-1 rounded-sm"
              onClick={() => stateDispatch({ type: "RESET_STATE" })}
            >
              <span
                className={clsx("text-center text-sm font-medium underline text-platform-blue-600 leading-tight", {
                  "opacity-50": hasDefaultFilters,
                })}
              >
                Reset filters
              </span>
            </button>
            <button
              disabled={hasNoFilters}
              className="flex items-center justify-end h-5 gap-1 rounded-sm"
              onClick={() => stateDispatch({ type: "REMOVE_ALL" })}
            >
              <span
                className={clsx("text-center text-sm font-medium underline text-platform-blue-600 leading-tight", {
                  "opacity-50": hasNoFilters,
                })}
              >
                Clear all filters
              </span>
            </button>
          </div>
          <div className="flex items-start justify-between pb-4">
            <div className="text-sm font-bold leading-tight text-black">Type:</div>
          </div>
          <div className="pb-4 space-y-2">
            <ClassificationToggles
              state={state}
              prefix="planning-selection"
              stateDispatch={stateDispatch}
              debouncedTrackControlChange={debouncedTrackControlChange}
            />
          </div>
          <DividerV2 />
          <PlanningApplicationsFilters
            columns={true}
            state={state}
            stateDispatch={stateDispatch}
            debouncedTrackControlChange={debouncedTrackControlChange}
          />
        </div>
      )}
    </>
  );
};

const PlanningApplicationsResults = ({
  layer,
  stateDispatch,
  applications,
  hasNoFilters,
  setDetailSelection,
}: Omit<LayerTypeSelectionSidebarProps, "selection"> & {
  stateDispatch: PlanningStateDispatch;
  applications: PlanningApplication[];
  hasNoFilters: boolean;
}) => {
  const { t } = useTranslation();

  const trackedSetApplicationDetailSelectionHandler = useTrackedApplicationSelectionHandler(setDetailSelection, {
    cardName: layer.title,
    clickDetail: "Planning application",
  });

  return (
    <div>
      {!hasNoFilters && (
        <div className="p-2">
          <div className="p-3 h-12 rounded-xl border border-[#e3e7eb] justify-start items-center inline-flex w-full">
            <div className="flex items-start justify-start pr-2">
              <Icon icon="icon-lt-filter-line" extraClasses="text-amber-600 pointer-events-none" />
            </div>
            <span className="flex gap-4 text-base font-medium leading-normal">Filters are applied</span>
            <Button
              data-testid="filter-planning"
              variant="ghost"
              className="ml-auto"
              onClick={() => stateDispatch({ type: "REMOVE_ALL" })}
            >
              {t("planning.planning_filters_panel.clear")}
            </Button>
          </div>
        </div>
      )}
      {applications.length > 0 && (
        <div className="p-2">
          <PlanningApplicationsByAddress
            planningApplications={applications}
            onApplicationClick={trackedSetApplicationDetailSelectionHandler}
          />
        </div>
      )}
    </div>
  );
};

function PlanningApplicationSelectionSidebarHeader({
  selection,
  defaultTitle,
}: Readonly<LayerTypeSelectionSidebarHeaderProps>) {
  const title = selection.type === SelectionType.PLANNING_APPLICATION ? `Other ${defaultTitle}` : defaultTitle;
  return <>{title}</>;
}

PlanningApplicationSelectionSidebar.Header = PlanningApplicationSelectionSidebarHeader;
